import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Config from "../../Config";
import { useSelector } from "react-redux";
import { useMutation } from "react-query";
import { IncomeExpenseSummarySkeleton } from "../../Components/SkeletonLoading";
import {
  BoxContainer,
  BoxTitle,
  BoxHead,
  Underline,
} from "../../Components/Styles/PageStyles";
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "react-tooltip";

const IncomeExpenseSummary = () => {
  const user = useSelector((state) => state.UserReducer.user);
  const property = useSelector(
    (state) => state.SelectPropertyReducer.selectProperty
  );
  const propId = property.id;
  const [apiData, setApiData] = useState([]);

  const fetchFunction = async () => {
    const response = await axios.get(
      `${Config.apiUrl}/property/income/expense/summary/web`,
      {
        headers: {
          authorization: "Bearer" + " " + user.token,
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
      }
    );
    return response.data;
  };

  const getSuccess = (res) => {
    if (res.isSuccess === false) error = true;
    else {
      setApiData(res?.data || []);
    }
  };

  const getError = (res) => { };

  const {
    isLoading: isLoading,
    mutate: getListMutate,
    error,
    data,
    isRefetching,
  } = useMutation(fetchFunction, {
    onSuccess: getSuccess,
    onError: getError,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      getListMutate();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [property]);

  return (
    <BoxContainer>
      <BoxHead>
        <BoxTitle className="!mb-0 whitespace-nowrap">
          Property-wise Summary
          <Underline />
        </BoxTitle>
      </BoxHead>
      {(isLoading || isRefetching) && <IncomeExpenseSummarySkeleton />}
      {!error && !isLoading && apiData.length > 0 && (
        <div className="gap-8 grid lg:grid-cols-2 pt-2">
          {apiData.map((monthData, index) => (
            <SummaryContainer key={`${monthData.month}-${monthData.year}`}>
              <MonthHeader>{monthData.propName}</MonthHeader>
              <TotalProfit>
                <span>
                  Total {monthData.netProfitLoss >= 0 ? "Profit" : "Loss"}
                </span>
                <Amount
                  className={
                    monthData.netProfitLoss >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  ₹{Math.abs(monthData.netProfitLoss)}
                </Amount>
              </TotalProfit>

              <IncomeSection>
                <SectionHeader className="bg-green-50 text-green-700">
                  <SectionTitle>Income</SectionTitle>
                  <TotalAmount>₹{monthData.totalIncome}</TotalAmount>
                </SectionHeader>
                <SummaryGrid>
                  <SummaryItem>
                    <Label>Rent Received</Label>
                    <Value>₹{monthData.totalRent}</Value>
                  </SummaryItem>
                  <SummaryItem>
                    <Label>Security</Label>
                    <Value>₹{monthData.totalSecurity}</Value>
                  </SummaryItem>
                  <SummaryItem>
                    <Label className="flex">
                      Others{" "}
                      <span
                        className="text-sm ml-2 mt-1 text-gray-500 font-normal "
                        data-tooltip-id="others-tooltip"
                        data-tooltip-content={`This section shows other income like fines, maintenance charges, electricity bills and similar things.`}
                      >
                        <IoMdInformationCircle />
                      </span>
                    </Label>
                    <Value>₹{monthData.otherIncome}</Value>
                  </SummaryItem>
                  <Tooltip id="others-tooltip" place="top" />
                  <SummaryItem>
                    <Label>Online</Label>
                    <Value className="flex items-center">
                      ₹{monthData.totalOnline}
                      <span
                        className="text-sm ml-2 text-gray-500 font-normal underline decoration-dotted"
                        data-tooltip-id="payu-tooltip"
                        data-tooltip-content={`₹${monthData?.totalPayu} collected via PayU out of total ₹${monthData?.totalOnline}`}
                      >
                        (₹{monthData?.totalPayu})
                      </span>
                    </Value>
                  </SummaryItem>
                  <Tooltip id="payu-tooltip" place="top" />
                  <SummaryItem>
                    <Label>Cash</Label>
                    <Value>₹{monthData.totalOffline}</Value>
                  </SummaryItem>
                </SummaryGrid>
              </IncomeSection>

              <ExpenseSection>
                <SectionHeader className="bg-red-50 text-red-700">
                  <SectionTitle>Expenses</SectionTitle>
                  <TotalAmount>₹{monthData.totalExpense}</TotalAmount>
                </SectionHeader>
                <SummaryGrid>
                  <SummaryItem>
                    <Label>Rent & Utilities</Label>
                    <Value>₹{monthData.totalBuildingRentExpense}</Value>
                  </SummaryItem>
                  <SummaryItem>
                    <Label>Food/Kitchen</Label>
                    <Value>₹{monthData.totalFoodExpense}</Value>
                  </SummaryItem>
                  <SummaryItem>
                    <Label>Staff/Salary</Label>
                    <Value>₹{monthData.totalStaffSalaryExpense}</Value>
                  </SummaryItem>
                  <SummaryItem>
                    <Label className="flex">
                      Others{" "}
                      <span
                        className="text-sm ml-2 mt-1 text-gray-500 font-normal "
                        data-tooltip-id="others-expense-tooltip"
                        data-tooltip-content={`This section includes only additional expenses, excluding rent, staff salaries and food costs.`}
                      >
                        <IoMdInformationCircle />
                      </span>
                    </Label>
                    <Value>₹{monthData.otherExpense}</Value>
                  </SummaryItem>
                  <Tooltip id="others-expense-tooltip" place="top" />
                  <SummaryItem>
                    <Label>Pending Rent</Label>
                    <Value>₹{monthData.totalDues}</Value>
                  </SummaryItem>
                </SummaryGrid>
              </ExpenseSection>
            </SummaryContainer>
          ))}
        </div>
      )}
    </BoxContainer>
  );
};

const SummaryContainer = tw.div`bg-white rounded-lg p-4 w-full shadow-sm`;
const MonthHeader = tw.h2`text-xl font-semibold text-gray-800 mb-2 underline underline-offset-4`;
const TotalProfit = tw.div`flex justify-between items-center mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200`;
const IncomeSection = tw.div`mb-3`;
const ExpenseSection = tw.div`mb-3`;
const SectionHeader = tw.div`flex justify-between items-center p-3 rounded-t-lg border-b border-gray-200 mb-3`;
const SectionTitle = tw.h3`text-lg font-semibold`;
const TotalAmount = tw.span`text-lg font-bold`;
const SummaryGrid = tw.div`grid grid-cols-2 md:grid-cols-3 gap-3`;
const SummaryItem = tw.div`p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors`;
const Label = tw.p`text-sm text-gray-500`;
const Value = tw.p`text-lg font-semibold text-gray-800`;
const Amount = tw.span`text-xl font-bold`;

export default IncomeExpenseSummary;
